import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Map as MapIcon,
  Timeline as TimelineIcon,
  NoteAdd as NoteIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart
} from 'recharts';
import { DatePicker } from '@material-ui/pickers';
import axios from 'axios';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  dateNavigator: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  dateDisplay: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    margin: '0 20px',
  },
  calendarIcon: {
    marginRight: theme.spacing(1),
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.shadows[3],
    },
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 500,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  jobCard: {
    height: '100%',
    transition: 'transform 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  jobCardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  jobTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  jobSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  jobStatus: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  completedChip: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    fontWeight: 500,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
  },
  chartContainer: {
    height: 280,
    marginBottom: theme.spacing(2),
  },
  miniChartContainer: {
    height: 150,
    marginTop: theme.spacing(1),
  },
  legendContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    marginRight: theme.spacing(0.5),
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
  },
  dialogContent: {
    padding: theme.spacing(2),
  },
  mapContainer: {
    height: 400,
    width: '100%',
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  mapPlaceholder: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  detailsContainer: {
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  infoItem: {
    padding: theme.spacing(1, 0),
  },
  infoLabel: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  infoValue: {
    marginTop: theme.spacing(0.5),
  },
  tabsContainer: {
    marginBottom: theme.spacing(2),
  },
  weatherInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  weatherItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  noJobs: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  navButton: {
    minWidth: 'auto',
  },
  locationInfo: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  chemicalItem: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  trendIcon: {
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
  },
  trendUp: {
    color: theme.palette.success.main,
  },
  trendDown: {
    color: theme.palette.error.main,
  },
  timeFrameSelector: {
    minWidth: 150,
  },
  jobsListContainer: {
    maxHeight: 400,
    overflow: 'auto',
  },
  dayGrid: {
    marginTop: theme.spacing(2),
  },
  dayCard: {
    position: 'relative',
    height: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: theme.shadows[3],
    },
  },
  todayCard: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  dayHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  dayNumber: {
    fontWeight: 600,
    fontSize: '1.5rem',
  },
  cardContentCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1.5),
  },
  countBadge: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold',
  },
  statsGrid: {
    marginBottom: theme.spacing(2),
  },
  viewSelector: {
    marginRight: theme.spacing(2),
  }
}));

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper style={{ padding: '10px' }}>
        <Typography variant="body2">{`${label}: ${payload[0].value} jobs`}</Typography>
      </Paper>
    );
  }
  return null;
};

// Tab Panel Component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`job-detail-tabpanel-${index}`}
      aria-labelledby={`job-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const CompletedJobsDashboard = ({ url, chemicals, jobsites }) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [timeFrame, setTimeFrame] = useState('month');
  const [viewMode, setViewMode] = useState('calendar');
  const [monthlyJobs, setMonthlyJobs] = useState([]);
  const [dailyJobs, setDailyJobs] = useState([]);
  const [jobsByTech, setJobsByTech] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [dailyChartData, setDailyChartData] = useState([]);
  const [calendarData, setCalendarData] = useState([]);

  // Fetch jobs for selected month
  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        // Format date for API
        const formattedDate = moment(selectedMonth).format('YYYY-MM-DD');
        
        // Replace with your actual API endpoint
        const response = await axios.get(`${url}/api/hmi/genericGetRequest`, {
          params: {
            type: 'COMPLETED_JOBS_BY_TECH',
            date: formattedDate
          }
        });
        
        // Set jobs from response
        if (response && response.data && response.data.data) {
          const jobs = response.data.data || [];
          setMonthlyJobs(jobs);
          
          // Process data for charts
          processJobsData(jobs);
        } else {
          setMonthlyJobs([]);
          resetChartData();
        }
      } catch (error) {
        console.error('Error fetching completed jobs:', error);
        setMonthlyJobs([]);
        resetChartData();
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [selectedMonth, url]);

  // Process jobs data for charts
  const processJobsData = (jobs) => {
    // Process data for daily chart
    const daysInMonth = moment(selectedMonth).daysInMonth();
    const monthStr = moment(selectedMonth).format('YYYY-MM');
    
    // Create an array for each day in the month
    const dailyData = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dateStr = `${monthStr}-${day.toString().padStart(2, '0')}`;
      return {
        date: dateStr,
        day: day,
        dayName: moment(dateStr).format('ddd'),
        jobs: 0
      };
    });
    
    // Create calendar data
    const startOfMonth = moment(selectedMonth).startOf('month');
    const endOfMonth = moment(selectedMonth).endOf('month');
    const firstDay = startOfMonth.day(); // 0 = Sunday, 1 = Monday, etc.
    
    // Generate calendar data (6 weeks × 7 days)
    const calendarDataTemp = [];
    let date = startOfMonth.clone().subtract(firstDay, 'days');
    
    for (let week = 0; week < 6; week++) {
      for (let day = 0; day < 7; day++) {
        calendarDataTemp.push({
          date: date.clone(),
          day: date.date(),
          month: date.month(),
          isCurrentMonth: date.month() === moment(selectedMonth).month(),
          isToday: date.isSame(moment(), 'day'),
          jobs: 0
        });
        date.add(1, 'day');
      }
    }

    // Count jobs by tech
    const techsMap = new Map();
    
    // Count jobs by day
    jobs.forEach(job => {
      if (job.work_complete_at) {
        const jobDate = moment(job.work_complete_at);
        
        // Update daily chart data
        if (jobDate.isBetween(startOfMonth, endOfMonth, 'day', '[]')) {
          const dayIndex = jobDate.date() - 1;
          if (dailyData[dayIndex]) {
            dailyData[dayIndex].jobs++;
          }
          
          // Update calendar data
          const calendarIndex = calendarDataTemp.findIndex(d => 
            d.isCurrentMonth && d.day === jobDate.date()
          );
          if (calendarIndex !== -1) {
            calendarDataTemp[calendarIndex].jobs++;
          }
        }
        
        // Update techs data
        const techId = job.work_completed_by ? job.work_completed_by._id : 'unknown';
        const techName = job.work_completed_by ? 
          `${job.work_completed_by.firstName} ${job.work_completed_by.lastName}` : 
          'Unknown Tech';
        
        if (!techsMap.has(techId)) {
          techsMap.set(techId, { name: techName, count: 0 });
        }
        techsMap.get(techId).count++;
      }
    });
    
    // Convert techs map to array for chart
    const techsArray = Array.from(techsMap, ([id, data]) => ({
      id,
      name: data.name,
      jobs: data.count
    })).sort((a, b) => b.jobs - a.jobs); // Sort by job count (descending)
    
    // Update state with processed data
    setDailyChartData(dailyData);
    setCalendarData(calendarDataTemp);
    setJobsByTech(techsArray);
    
    // Filter jobs for the selected day
    filterJobsForSelectedDay(jobs);
  };
  
  // Filter jobs for selected day
  const filterJobsForSelectedDay = (allJobs) => {
    const startOfDay = moment(selectedDate).startOf('day');
    const endOfDay = moment(selectedDate).endOf('day');
    
    const filteredJobs = allJobs.filter(job => {
      const jobDate = moment(job.work_complete_at);
      return jobDate.isBetween(startOfDay, endOfDay, 'day', '[]');
    });
    
    setDailyJobs(filteredJobs);
  };
  
  // Reset chart data when no jobs
  const resetChartData = () => {
    setDailyChartData([]);
    setCalendarData([]);
    setJobsByTech([]);
    setDailyJobs([]);
  };

  // Update daily jobs when selected date changes
  useEffect(() => {
    filterJobsForSelectedDay(monthlyJobs);
  }, [selectedDate]);

  // Mock data for development
  useEffect(() => {
    // This is just for development, remove in production
    if (process.env.NODE_ENV === 'development' && monthlyJobs.length === 0 && !loading) {
      const mockJobs = [];
      
      // Generate mock data for the current month
      const daysInMonth = moment(selectedMonth).daysInMonth();
      const monthStr = moment(selectedMonth).format('YYYY-MM');
      
      // Create random jobs for each day
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${monthStr}-${day.toString().padStart(2, '0')}`;
        const jobCount = Math.floor(Math.random() * 5); // 0-4 jobs per day
        
        for (let i = 0; i < jobCount; i++) {
          const jobDate = moment(dateStr).add(8 + i, 'hours').toDate();
          
          mockJobs.push({
            _id: `mock-${dateStr}-${i}`,
            work_complete_at: jobDate,
            jobSite: {
              _id: `site-${i}`,
              name: `Jobsite ${day}-${i}`,
              address: `${100 + i} Main St, City`,
              location: { lat: 32.7767, lng: -96.7970 }
            },
            crew: {
              _id: `crew-${i % 3}`,
              name: `Crew ${i % 3 + 1}`,
              foremanName: `Foreman ${i % 3 + 1}`
            },
            work_completed_by: {
              _id: `tech-${i % 5}`,
              firstName: `Tech`,
              lastName: `${i % 5 + 1}`
            },
            notes: `Job notes for ${dateStr} #${i}`,
            chemicalsUsed: [
              { name: 'RoundUp Pro', amount: '2.5 gallons', concentration: '2%' },
              { name: 'Surfactant', amount: '8 oz', concentration: '0.5%' }
            ],
            weather: {
              temp: `${70 + Math.floor(Math.random() * 10)}°F`,
              windSpeed: `${Math.floor(Math.random() * 10)} mph`,
              windDir: ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.floor(Math.random() * 8)],
              conditions: ['Sunny', 'Partly Cloudy', 'Cloudy', 'Rainy'][Math.floor(Math.random() * 4)]
            }
          });
        }
      }
      
      setMonthlyJobs(mockJobs);
      processJobsData(mockJobs);
    }
  }, [monthlyJobs.length, loading, selectedMonth]);

  // Handle navigation
  const handlePreviousMonth = () => {
    setSelectedMonth(prevDate => moment(prevDate).subtract(1, 'month').toDate());
  };

  const handleNextMonth = () => {
    setSelectedMonth(prevDate => moment(prevDate).add(1, 'month').toDate());
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const handleTimeFrameChange = (event) => {
    setTimeFrame(event.target.value);
  };

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
    setTabValue(0); // Reset to first tab when opening dialog
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format time helpers
  const formatTime = (timeString) => {
    try {
      return moment(timeString).format('h:mm A');
    } catch (error) {
      return timeString;
    }
  };

  const formatDuration = (startTime, stopTime) => {
    try {
      const start = moment(startTime);
      const stop = moment(stopTime);
      const duration = moment.duration(stop.diff(start));
      const hours = Math.floor(duration.asHours());
      const mins = Math.floor(duration.asMinutes() % 60);
      
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      } else {
        return `${mins}m`;
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  // Calculate stats
  const totalJobsMonth = monthlyJobs.length;
  const totalJobsToday = dailyJobs.length;
  
  // Calculate previous month stats for comparison
  const prevMonthJobs = 0; // This would come from an API call
  const monthlyChange = prevMonthJobs > 0 ? 
    Math.round(((totalJobsMonth - prevMonthJobs) / prevMonthJobs) * 100) : 0;

  // Calculate job distribution
  const weekdayJobs = dailyChartData.filter(d => !['Sat', 'Sun'].includes(d.dayName))
    .reduce((sum, day) => sum + day.jobs, 0);
  const weekendJobs = dailyChartData.filter(d => ['Sat', 'Sun'].includes(d.dayName))
    .reduce((sum, day) => sum + day.jobs, 0);

  // Render calendar view
  const renderCalendarView = () => {
    // Group calendar data by weeks
    const weeks = [];
    for (let i = 0; i < calendarData.length; i += 7) {
      weeks.push(calendarData.slice(i, i + 7));
    }
    
    return (
      <div className={classes.dayGrid}>
        <Grid container spacing={1}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
            <Grid item xs key={index}>
              <Typography variant="body2" align="center" color="textSecondary">
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>
        
        {weeks.map((week, weekIndex) => (
          <Grid container spacing={1} key={weekIndex} style={{ marginTop: 4 }}>
            {week.map((day, dayIndex) => (
              <Grid item xs key={dayIndex}>
                <Card 
                  className={`${classes.dayCard} ${day.isToday ? classes.todayCard : ''}`}
                  style={{ 
                    opacity: day.isCurrentMonth ? 1 : 0.3,
                    backgroundColor: moment(day.date).isSame(selectedDate, 'day') ? '#f0f7ff' : 'white'
                  }}
                  onClick={() => day.isCurrentMonth && handleDayClick(day.date.toDate())}
                >
                  {day.jobs > 0 && (
                    <div className={classes.countBadge}>{day.jobs}</div>
                  )}
                  <div className={classes.cardContentCenter}>
                    <Typography variant="h6" className={classes.dayNumber}>
                      {day.day}
                    </Typography>
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
        ))}
      </div>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5">Completed Jobs Dashboard</Typography>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" className={classes.viewSelector}>
            <InputLabel id="view-mode-label">View</InputLabel>
            <Select
              labelId="view-mode-label"
              value={viewMode}
              onChange={handleViewModeChange}
              label="View"
            >
              <MenuItem value="calendar">Calendar</MenuItem>
              <MenuItem value="list">List</MenuItem>
              <MenuItem value="charts">Charts</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.timeFrameSelector}>
            <InputLabel id="time-frame-label">Time Frame</InputLabel>
            <Select
              labelId="time-frame-label"
              value={timeFrame}
              onChange={handleTimeFrameChange}
              label="Time Frame"
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
      
      {/* Date Navigator */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <div className={classes.dateNavigator}>
          <Button
            variant="outlined"
            onClick={handlePreviousMonth}
            className={classes.navButton}
            startIcon={<ChevronLeftIcon />}
          >
            Prev
          </Button>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DatePicker
              autoOk
              disableToolbar
              variant="inline"
              value={selectedMonth}
              onChange={handleMonthChange}
              views={['month']}
              format="MMMM yyyy"
              label={
                <Typography className={classes.dateDisplay}>
                  <CalendarIcon className={classes.calendarIcon} />
                  {moment(selectedMonth).format('MMMM YYYY')}
                </Typography>
              }
            />
            
            {timeFrame === 'day' && (
              <DatePicker
                autoOk
                disableToolbar
                variant="inline"
                value={selectedDate}
                onChange={handleDateChange}
                format="MMM d, yyyy"
                label="Select date"
                style={{ marginLeft: 16 }}
              />
            )}
          </div>
          
          <Button
            variant="outlined"
            onClick={handleNextMonth}
            className={classes.navButton}
            endIcon={<ChevronRightIcon />}
          >
            Next
          </Button>
        </div>
        
        {/* Stats Grid */}
        <Grid container spacing={2} className={classes.statsGrid}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.jobCardContent}>
                <div className={classes.cardHeader}>
                  <Typography color="textSecondary">Monthly Jobs</Typography>
                  {monthlyChange !== 0 && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" color="textSecondary">
                        {monthlyChange > 0 ? '+' : ''}{monthlyChange}%
                      </Typography>
                      {monthlyChange > 0 ? (
                        <ArrowUpwardIcon className={`${classes.trendIcon} ${classes.trendUp}`} />
                      ) : (
                        <ArrowDownwardIcon className={`${classes.trendIcon} ${classes.trendDown}`} />
                      )}
                    </div>
                  )}
                </div>
                <Typography className={classes.statsValue}>{totalJobsMonth}</Typography>
                <div className={classes.miniChartContainer}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyChartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                      <Area type="monotone" dataKey="jobs" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.jobCardContent}>
                <Typography color="textSecondary">Top Technician</Typography>
                <Typography className={classes.statsValue}>
                  {jobsByTech.length > 0 ? jobsByTech[0].name : 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {jobsByTech.length > 0 ? `${jobsByTech[0].jobs} completed jobs` : 'No jobs completed'}
                </Typography>
                {jobsByTech.length > 0 && (
                  <div className={classes.miniChartContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={jobsByTech.slice(0, 3)}>
                        <Bar dataKey="jobs" fill="#8884d8">
                          {jobsByTech.slice(0, 3).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <CardContent className={classes.jobCardContent}>
                <Typography color="textSecondary">Distribution</Typography>
                <Typography className={classes.statsValue}>
                  {weekdayJobs + weekendJobs > 0 ? 
                    `${Math.round((weekdayJobs / (weekdayJobs + weekendJobs)) * 100)}%` : 
                    'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Weekday vs Weekend
                </Typography>
                {weekdayJobs + weekendJobs > 0 && (
                  <div className={classes.miniChartContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Weekday', value: weekdayJobs },
                            { name: 'Weekend', value: weekendJobs }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          dataKey="value"
                        >
                          <Cell fill="#0088FE" />
                          <Cell fill="#00C49F" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Main Content */}
      <Card>
        <CardContent>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : viewMode === 'calendar' ? (
            renderCalendarView()
          ) : viewMode === 'charts' ? (
            <div>
              <Typography variant="h6" gutterBottom>
                Completed Jobs by Day
              </Typography>
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dailyChartData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day" 
                      tickFormatter={(value, index) => `${value} ${dailyChartData[index]?.dayName}`} 
                    />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="jobs" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <Divider style={{ margin: '24px 0' }} />
              
              <Typography variant="h6" gutterBottom>
                Jobs by Technician
              </Typography>
              <div className={classes.chartContainer}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={jobsByTech}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={120}
                    />
                    <Tooltip />
                    <Bar dataKey="jobs" fill="#82ca9d">
                      {jobsByTech.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div>
              <Typography variant="subtitle1" gutterBottom>
                {dailyJobs.length > 0 ? (
                  `Completed Jobs for ${moment(selectedDate).format('MMMM D, YYYY')} (${dailyJobs.length})`
                ) : (
                  `No jobs completed on ${moment(selectedDate).format('MMMM D, YYYY')}`
                )}
              </Typography>
              
              {dailyJobs.length > 0 ? (
                <div className={classes.jobsListContainer}>
                  <Grid container spacing={2}>
                    {dailyJobs.map((job) => (
                      <Grid item xs={12} sm={6} md={4} key={job._id}>
                        <Card 
                          className={classes.jobCard}
                          onClick={() => handleJobClick(job)}
                        >
                          <CardContent className={classes.jobCardContent}>
                            <Typography variant="h6" className={classes.jobTitle}>
                              {job.jobSite.name}
                            </Typography>
                            <Typography variant="body2" className={classes.jobSubtitle}>
                              {job.crew.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {job.jobSite.address}
                            </Typography>
                            
                            <div className={classes.jobStatus}>
                              <Typography variant="body2">
                                {formatTime(job.work_complete_at)}
                              </Typography>
                              <div className={classes.completedChip}>Completed</div>
                            </div>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              ) : (
                <Paper className={classes.noJobs}>
                  <Typography variant="body1">
                    No completed jobs found for this date
                  </Typography>
                </Paper>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      {selectedJob && (
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          fullWidth 
          maxWidth="md"
          aria-labelledby="job-details-dialog"
        >
          <div className={classes.dialogTitle}>
            <DialogTitle id="job-details-dialog">
              {selectedJob.jobSite.name}
            </DialogTitle>
            <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
              <CloseIcon />
            </IconButton>
          </div>
          
          {/* Tabs for Map and Spray Details */}
          <AppBar position="static" color="default" elevation={0}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Map View" icon={<MapIcon />} />
              <Tab label="Spray Details" icon={<TimelineIcon />} />
            </Tabs>
          </AppBar>
          
          <DialogContent className={classes.dialogContent}>
            {/* Map Tab */}
            <TabPanel value={tabValue} index={0}>
              <div className={classes.mapContainer}>
                <div className={classes.mapPlaceholder}>
                  <MapIcon style={{ fontSize: 48, opacity: 0.5 }} />
                  <Typography variant="body1">
                    Map would be rendered here showing spray areas, start/stop locations, and jobsite perimeter
                  </Typography>
                </div>
              </div>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <MapIcon className={classes.sectionIcon} />
                    Location Information
                  </Typography>
                  
                  <Paper className={classes.locationInfo}>
                    <Typography variant="body2" className={classes.infoLabel}>
                      Job Location:
                    </Typography>
                    <Typography variant="body2" className={classes.infoValue}>
                      {selectedJob.jobSite?.address}
                    </Typography>
                    
                    {selectedJob.jobSite?.location && (
                      <>
                        <Divider style={{ margin: '8px 0' }} />
                        <Typography variant="body2" className={classes.infoLabel}>
                          Coordinates:
                        </Typography>
                        <Typography variant="body2" className={classes.infoValue}>
                          Lat: {selectedJob.jobSite.location.lat}, Lng: {selectedJob.jobSite.location.lng}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Weather Conditions
                  </Typography>
                  
                  <div className={classes.weatherInfo}>
                    {selectedJob.weather ? (
                      <>
                        <div className={classes.weatherItem}>
                          <Typography variant="body2" className={classes.infoLabel}>Temperature:</Typography>
                          <Typography variant="body2">{selectedJob.weather?.temp}</Typography>
                        </div>
                        
                        <div className={classes.weatherItem}>
                          <Typography variant="body2" className={classes.infoLabel}>Wind Speed:</Typography>
                          <Typography variant="body2">{selectedJob.weather?.windSpeed}</Typography>
                        </div>
                        
                        <div className={classes.weatherItem}>
                          <Typography variant="body2" className={classes.infoLabel}>Wind Direction:</Typography>
                          <Typography variant="body2">{selectedJob.weather?.windDir}</Typography>
                        </div>
                        
                        <div className={classes.weatherItem}>
                          <Typography variant="body2" className={classes.infoLabel}>Conditions:</Typography>
                          <Typography variant="body2">{selectedJob.weather?.conditions}</Typography>
                        </div>
                      </>
                    ) : (
                      <Typography variant="body2">Weather data not available</Typography>
                    )}
                  </div>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Spray Details Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <TimelineIcon className={classes.sectionIcon} />
                    Time Details
                  </Typography>
                  
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Completed At:</TableCell>
                          <TableCell>{formatTime(selectedJob.work_complete_at)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Technician:</TableCell>
                          <TableCell>
                            {selectedJob.work_completed_by ? 
                              `${selectedJob.work_completed_by.firstName} ${selectedJob.work_completed_by.lastName}` : 
                              'Unknown'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Crew:</TableCell>
                          <TableCell>{selectedJob.crew?.name || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className={classes.infoLabel}>Foreman:</TableCell>
                          <TableCell>{selectedJob.crew?.foremanName || 'Unknown'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Chemicals Used
                  </Typography>
                  
                  {selectedJob.chemicalsUsed && selectedJob.chemicalsUsed.length > 0 ? (
                    <div>
                      {selectedJob.chemicalsUsed.map((chemical, index) => (
                        <div key={index} className={classes.chemicalItem}>
                          <Typography variant="body1">{chemical.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            Amount: {chemical.amount} • Concentration: {chemical.concentration}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2">No chemicals recorded for this job</Typography>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    <NoteIcon className={classes.sectionIcon} />
                    Notes
                  </Typography>
                  
                  <Paper style={{ padding: '16px' }}>
                    {selectedJob.notes ? (
                      <Typography variant="body2">{selectedJob.notes}</Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No notes recorded for this job</Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default CompletedJobsDashboard;
       